import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import routes, {
  kioskRoutes,
  presentationRoutes,
  routesForAuthenticatedOnly,
  routesForNotAuthenticatedOnly,
  routesForSuperAdminOnly,
  screenRoutes,
  templateRoutes,
  productRoutes,
  dsokRoutes,
} from "../../../app/routes";
import routePathMatch from "@jumbo/hooks/useRoutePathMatch";
import {
  getMainAuth,
  removeAuth,
  storeToken,
  storeUser,
  storeVersion,
} from "./authHelpers";
import { config } from "../../../app/config/main";
import { AuthContext } from "@jumbo/components/JumboAuthProvider/JumboAuthContext";
import { clearImpersonationOrigin } from "../../../app/utils/impersonationOrigin";
import {
  ACCESS_SYSTEM_TICKETS_PATH,
  isPathAllowedForEasyAccessOperator,
} from "../../../app/utils/constants/roleMenuConfig";

const APP_VERSION = process.env.REACT_APP_VERSION;

const storedToken = localStorage.getItem("token");
const storedUser = localStorage.getItem("user");
const storedVersion = localStorage.getItem("DV_VERSION");

let firstTimePageLoad = true;

const init = () => {
  if (!config?.authSetting) {
    throw Error(
      `You are using JumboAuthProvider but you haven't setup authSetting inside /src/app/config/main.js's config object`
    );
  }

  if (storedToken) {
    storeToken(storedToken); // also sets axios header
  }

  return {
    authToken: storedToken ?? null,
    authUser: storedUser,
    isLoading: false,
    fallbackPath: config.authSetting.fallbackPath,
  };
};

const authReducer = (state, action) => {
  switch (action.type) {
    case "set-auth-data":
      return {
        ...state,
        ...action.payload,
      };

    case "start-loading":
      return {
        ...state,
        isLoading: true,
      };

    case "stop-loading":
      return {
        ...state,
        isLoading: false,
      };
  }
};

const JumboAuthProvider = ({ children, ...restProps }) => {
  const [authOptions, setAuthOptions] = React.useReducer(
    authReducer,
    restProps,
    init
  );
  const multilicenseLoggedRef = React.useRef(null);
  const routesForSuperAdminOnlyArr = [...routesForSuperAdminOnly];
  const templateRoutesArr = [...templateRoutes];
  const presentationRoutesArr = [...presentationRoutes];
  const productRoutesArr = [...productRoutes];
  const kioksRoutesArr = [...kioskRoutes];
  const dsokRoutesArr = [...dsokRoutes];
  const screenRoutesArr = [...screenRoutes];
  const location = useLocation();
  const storedToken = localStorage.getItem("token");
  const [isAuthenticatedRouteOnly, setIsAuthenticatedRouteOnly] = useState();
  const [isNotAuthenticatedRouteOnly, setIsNotAuthenticatedRouteOnly] =
    useState();
  const [isSuperAdminOnly, setIsSuperAdminOnly] = useState();
  const [isPresentationRoute, setIsPresentationRoute] = useState();
  const [isKioskRoute, setIsKioskRoute] = useState();
  const [isDsokRoute, setIsDsokRoute] = useState();
  const [isTemplateRoute, setIsTemplateRoute] = useState();
  const [isProductRoute, setIsProductRoute] = useState();
  const [isScreenRoute, setIsScreenRoute] = useState();
  const [logout, setLogout] = React.useState(!storedToken);
  const navigate = useNavigate();

  useEffect(() => {
    if (location?.pathname) {
      const authenticatedRouteOnly = routePathMatch(
        location,
        routesForAuthenticatedOnly
      );
      setIsAuthenticatedRouteOnly(authenticatedRouteOnly);

      const superAdminOnly = routePathMatch(
        location,
        routesForSuperAdminOnlyArr
      );

      setIsSuperAdminOnly(superAdminOnly);

      const notAuthenticatedRouteOnly = routePathMatch(
        location,
        routesForNotAuthenticatedOnly
      );

      setIsNotAuthenticatedRouteOnly(notAuthenticatedRouteOnly);

      const presentationRoute = routePathMatch(location, presentationRoutesArr);

      setIsPresentationRoute(presentationRoute);

      const templateRoute = routePathMatch(location, templateRoutesArr);

      setIsTemplateRoute(templateRoute);

      const productRoute = routePathMatch(location, productRoutesArr);

      setIsProductRoute(productRoute);

      const kioskRoute = routePathMatch(location, kioksRoutesArr);

      setIsKioskRoute(kioskRoute);

      const dsokRoute = routePathMatch(location, dsokRoutesArr);

      setIsDsokRoute(dsokRoute);

      const screenRoute = routePathMatch(location, screenRoutesArr);

      setIsScreenRoute(screenRoute);
    }
  }, [
    location,
    location?.pathname,
    routesForSuperAdminOnlyArr,
    kioksRoutesArr,
    dsokRoutesArr,
    templateRoutesArr,
    presentationRoutesArr,
    screenRoutesArr,
  ]);

  React.useEffect(() => {
    if (logout) {
      removeAuth();
      setAuthOptions({
        type: "set-auth-data",
        payload: { authToken: null, authUser: null, isLoading: false },
      });
      setLogout(false);
    }
  }, [logout]);

  React.useEffect(() => {
    if (!storedToken) {
      setLogout(true);
    }
  }, [storedToken]);

  React.useEffect(() => {
    if (APP_VERSION !== storedVersion) {
      storeVersion(APP_VERSION);
      setLogout(true);
    }
  }, [storedVersion]);

  const setAuthToken = React.useCallback(async (response, temporary) => {
    if (!response) {
      setLogout(true);
      return;
    }
    const { token, data } = response;
    if (!token || !data) {
      setLogout(true);
      return;
    }
    if (!temporary) {
      clearImpersonationOrigin();
    }
    const { user, customer } = data;
    const { username, firstName, lastName, email, customerId, userId, type } =
      user;
    const mainAuth = temporary && getMainAuth();

    setAuthOptions({ type: "start-loading" });

    storeToken(token);
    try {
      const authUser = {
        role: "user",
        username,
        firstName,
        lastName,
        email,
        customerId,
        userId,
        customer,
        mainAuth,
        type,
      }; // here we can store roles etc.
      setAuthOptions({
        type: "set-auth-data",
        payload: { authToken: token, authUser: authUser, isLoading: false },
      });
      console.log("Storing user data:", authUser);
      storeUser(authUser);
      console.log("User data stored successfully");
      return;
    } catch (error) {
      setLogout(true);
      console.error(error);
    }
  }, []);

  //todo: maybe in next version
  const setRedirectPath = React.useCallback((redirectPath) => {
    setAuthOptions({ type: "set-redirect-path", payload: { redirectPath } });
  }, []);

  const setAuthData = React.useCallback((data) => {
    setAuthOptions({ type: "set-auth-data", payload: data });
  }, []);

  const getAuthUser = () => {
    try {
      return JSON.parse(authOptions.authUser);
    } catch (error) {
      return authOptions.authUser;
    }
  };

  // Multi-license detection + logging (no UI).
  React.useEffect(() => {
    const authUser = getAuthUser();
    const customer = authUser?.customer;
    if (!customer) return;

    const safeParse = (maybeJson) => {
      if (!maybeJson) return null;
      if (typeof maybeJson === "object") return maybeJson;
      if (typeof maybeJson !== "string") return null;
      try {
        return JSON.parse(maybeJson);
      } catch (_e) {
        return null;
      }
    };

    const dsokSettingsParsed = safeParse(customer?.dsokSettings);
    const relation =
      dsokSettingsParsed?.customer?.customerRelation ??
      customer?.customerRelation ??
      null;

    if (relation?.relationshipType !== "multiLicense") return;

    // IMPORTANT: parentCustomerId is usually stored as a customer "identifier"
    // (often URL-like string), not necessarily numeric customerId.
    // Prefer `url` when determining currentCustomerId/parentId.
    const getPrimaryCustomerIdentifier = (c) => {
      const candidates = [
        c?.url,
        c?.customerUrl,
        c?.customer?.url,
        c?.customerId,
        c?.id,
        c?._id,
      ]
        .filter((v) => v !== undefined && v !== null)
        .map((v) => String(v).trim())
        .filter((v) => v.length > 0);
      return candidates[0] ?? "";
    };

    const currentCustomerIdStr = getPrimaryCustomerIdentifier(
      dsokSettingsParsed?.customer ?? customer
    );
    if (!currentCustomerIdStr) return;

    // Resolve parentId exactly per spec, but for parent customers we may need to
    // pick the identifier that children actually reference (often _id/hash).
    const parentIdFromRelation =
      relation?.customerType === "child" && relation?.parentCustomerId
        ? String(relation.parentCustomerId)
        : relation?.customerType === "parent"
          ? currentCustomerIdStr
          : "";
    if (!parentIdFromRelation) return;

    // Log once per login/bootstrap.
    const logKey = `${authOptions?.authToken ?? ""}:multilicense`;
    if (multilicenseLoggedRef.current === logKey) return;
    multilicenseLoggedRef.current = logKey;

    const getCustomerIdentifierCandidates = (c) => {
      const ds = safeParse(c?.dsokSettings);
      const candidates = [
        c?.url,
        ds?.customer?.url,
        ds?.customer?.customerUrl,
        c?.customerUrl,
        ds?.customer?.customerId,
        c?.customerId,
        c?.id,
        c?._id,
      ]
        .filter((v) => v !== undefined && v !== null)
        .map((v) => String(v).trim())
        .filter((v) => v.length > 0);
      return Array.from(new Set(candidates));
    };

    const getDisplayName = (c) => {
      const raw =
        c?.companyName ??
        c?.customerName ??
        c?.name ??
        c?.title ??
        c?.storeCode ??
        c?.code ??
        c?.customerCode ??
        "";
      const trimmed = String(raw || "").trim();
      return trimmed.length > 0 ? trimmed : getCustomerIdentifierCandidates(c)[0] ?? "";
    };

    const resolveRelationFromCustomer = (c) => {
      const ds = safeParse(c?.dsokSettings);
      return ds?.customer?.customerRelation ?? c?.customerRelation ?? null;
    };

    const loadAndLog = async () => {
      try {
        // Ensure the axios instance points to DV backend.
        const base = (process.env.REACT_APP_DATAVISION_BACKEND_URL || "").trim();
        if (!base) throw new Error("Missing REACT_APP_DATAVISION_BACKEND_URL");
        config.authSetting.axiosObject.defaults.baseURL = `${base}/V1`;

        const { data } = await config.authSetting.axiosObject.get(
          `/customers/list?customerId=1`
        );
        const response = data?.response;
        const rawList = Array.isArray(response) ? response : [];
        const customersData = rawList.map((item) => ({
          ...(item?.customerData || {}),
          users: item?.users,
          dsokSettings: item?.customerData?.dsokSettings,
          customerRelation: item?.customerData?.customerRelation,
        }));

        const currentIdsFromAuth = getCustomerIdentifierCandidates(
          dsokSettingsParsed?.customer ?? customer
        );

        const resolveParentIdFromDataset = () => {
          if (relation?.customerType === "child") return String(parentIdFromRelation);

          // For parent: choose the identifier that is used by children in parentCustomerId.
          // 1) Find the dataset entry that corresponds to the logged-in customer (best effort).
          const selfMatch =
            customersData.find((c) => {
              const ids = getCustomerIdentifierCandidates(c);
              return ids.some((id) => currentIdsFromAuth.includes(String(id)));
            }) || null;

          const parentCandidates = selfMatch
            ? getCustomerIdentifierCandidates(selfMatch)
            : currentIdsFromAuth;

          // 2) Pick the candidate that yields the most children.
          let best = parentCandidates[0] ?? String(parentIdFromRelation);
          let bestCount = -1;
          for (const cand of parentCandidates) {
            const count = customersData.filter((c) => {
              const rel = resolveRelationFromCustomer(c);
              return (
                rel?.customerType === "child" &&
                rel?.relationshipType === "multiLicense" &&
                String(rel?.parentCustomerId ?? "") === String(cand)
              );
            }).length;
            if (count > bestCount) {
              bestCount = count;
              best = cand;
            }
          }
          return best;
        };

        const resolvedParentId = resolveParentIdFromDataset();

        const inGroup = customersData.filter((c) => {
          const rel = resolveRelationFromCustomer(c);
          const ids = getCustomerIdentifierCandidates(c);

          // parent
          if (ids.includes(String(resolvedParentId))) return true;

          // children
          if (rel?.customerType !== "child") return false;
          if (String(rel?.parentCustomerId ?? "") !== String(resolvedParentId))
            return false;
          return rel?.relationshipType === "multiLicense";
        });

        const parent = inGroup.find((c) =>
          getCustomerIdentifierCandidates(c).includes(String(resolvedParentId))
        );
        const children = inGroup
          .filter(
            (c) =>
              !getCustomerIdentifierCandidates(c).includes(String(resolvedParentId))
          )
          .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));

        const sorted = [parent, ...children].filter(Boolean);

        const customers = sorted.map((c) => {
          const rel = resolveRelationFromCustomer(c);
          const ids = getCustomerIdentifierCandidates(c);
          const isParent = ids.includes(String(resolvedParentId));
          const id = isParent ? String(resolvedParentId) : ids[0] ?? "";
          const firstUser = Array.isArray(c?.users) ? c.users[0] : null;
          const username = firstUser?.username ?? null;
          const passwordHash = firstUser?.password ?? null;
          const passwordHashStr =
            passwordHash != null ? String(passwordHash) : null;
          const redact = (s) => {
            if (typeof s !== "string") return null;
            const trimmed = s.trim();
            if (trimmed.length <= 12) return "***";
            return `${trimmed.slice(0, 6)}…${trimmed.slice(-4)}`;
          };
          return {
            id,
            name: getDisplayName(c),
            customerType: isParent ? "parent" : "child",
            parentCustomerId: isParent
              ? null
              : String(rel?.parentCustomerId ?? resolvedParentId),
            loginAs: {
              // Matches what `handleLoginAsCustomer(customer)` consumes.
              // NOTE: Password hash is sensitive; we redact it in logs.
              user: {
                username,
                type: "hash",
                passwordHashRedacted: redact(passwordHashStr),
                passwordHashLength:
                  typeof passwordHashStr === "string"
                    ? passwordHashStr.length
                    : null,
                hasPasswordHash: Boolean(passwordHashStr),
              },
              hasRequiredFields: Boolean(username && passwordHashStr),
            },
          };
        });

        const multilicense = {
          enabled: true,
          parentId: String(resolvedParentId),
          currentCustomerId: currentCustomerIdStr,
          loginAsRequirements: {
            functionLocation: [
              "src/app/pages/customers/Customers.js#handleLoginAsCustomer",
            ],
            requiredArgs: ["customer"],
            requiredCustomerFields: ["customer.users[0].username", "customer.users[0].password"],
            requiredStorageKeys: ["localStorage.token", "localStorage.user"],
            requiredContextUpdates: [
              "useJumboAuth.setAuthToken(response, true)",
              "JumboAuthProvider.storeToken(token) -> sets axios Authorization headers",
              "JumboAuthProvider.storeUser(authUser) -> persists auth context",
            ],
            apiCalls: [
              {
                method: "POST",
                url: "/auth/login",
                purpose: "Login as selected customer user (hash mode)",
                requiredPayloadFields: ["username", "password", "type=hash"],
                responseFieldsUsed: ["token", "data.user", "data.customer.url"],
              },
              {
                method: "GET",
                url: "/customer?customer=<customerUrl>",
                purpose: "Load customer data after login-as (fetch DSOK BE Domain)",
                requiredPayloadFields: ["customerUrl"],
                responseFieldsUsed: ["response (customer object, including dsokSettings)"],
              },
            ],
          },
          customers,
        };

        console.log("multilicense", multilicense);
      } catch (_e) {
        console.log("multilicense", {
          enabled: true,
          error: true,
          parentId: String(parentIdFromRelation),
        });
      }
    };

    loadAndLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authOptions?.authToken]);

  const contextValue = React.useMemo(() => {
    return {
      ...authOptions,
      setAuthData,
      setRedirectPath,
      setAuthToken,
      setAuthOptions,
      getAuthUser,
    };
  }, [authOptions]);

  React.useEffect(() => {
    if (!authOptions.authToken) {
      if (isAuthenticatedRouteOnly) {
        navigate(authOptions?.fallbackPath);
      }
    } else if (!authOptions.authUser) {
      // Validate stored token by fetching current user
      config.authSetting
        .getAuthUserService()
        .then((response) => {
          if (response.token && response.data) {
            setAuthToken(response);
          } else {
            // Auth validation format: { user, customer } - create the expected structure
            const authResponse = {
              token: authOptions.authToken,
              data: response,
            };
            setAuthToken(authResponse);
          }
        })
        .catch((error) => {
          setLogout(true);
        });
    } else if (isNotAuthenticatedRouteOnly) {
      if (!firstTimePageLoad)
        navigate(config.authSetting.redirectNotAuthenticatedPath ?? "/");
      else firstTimePageLoad = false;
    } else {
      let authUser;
      try {
        authUser = JSON.parse(authOptions.authUser);
      } catch (e) {
        authUser = authOptions.authUser;
      }
      if (authUser?.type === 6) {
        if (!isPathAllowedForEasyAccessOperator(location.pathname)) {
          navigate(ACCESS_SYSTEM_TICKETS_PATH);
        }
        return;
      }
    }
    if (isSuperAdminOnly) {
      let authUser;
      try {
        authUser = JSON.parse(authOptions.authUser);
      } catch (error) {
        authUser = authOptions.authUser;
      }
      const customerType =
        authUser?.customer?.customerType ?? authUser?.customer?.customer_type;
      const isServicePartner = customerType === 7;
      const isDsokCustomersPage =
        location.pathname === "/dsok/customers";
      const isCustomerDsokDetailPage =
        /^\/customer\/[^/]+\/(dsok|smartkiosk)$/.test(
          location.pathname || ""
        ) ||
        /^\/dsok\/customer-detail\/[^/]+(\/.*)?$/.test(
          location.pathname || ""
        );
      const allowed =
        customerType === 1 ||
        customerType === 2 ||
        (isServicePartner &&
          (isDsokCustomersPage || isCustomerDsokDetailPage));
      if (!allowed) {
        navigate("/");
      }
    } else if (
      isPresentationRoute ||
      isKioskRoute ||
      isTemplateRoute ||
      isProductRoute ||
      isDsokRoute
    ) {
      let authUser;
      try {
        authUser = JSON.parse(authOptions.authUser);
      } catch (error) {
        authUser = authOptions.authUser;
      }
      const dsokOperator = authUser?.type === 4;
      const easyPayOperator = authUser?.type === 5;
      const kiosk = authUser?.customer?.kiosk;
      const dsok = authUser?.customer?.dsok;
      const presentations = authUser?.customer?.presentations;
      const templates = authUser?.customer?.templates;
      const products = authUser?.customer?.products;
      const screens = authUser?.customer?.screens;
      if (isKioskRoute && !kiosk) {
        navigate("/");
        return;
      }
      if (dsokOperator && !isDsokRoute) {
        navigate("/");
        return;
      }
      if (easyPayOperator && !isDsokRoute) {
        navigate("/");
        return;
      }
      if (isDsokRoute && !dsok) {
        navigate("/");
        return;
      }
      if (isPresentationRoute && !presentations) {
        navigate("/");
        return;
      }
      if (isTemplateRoute && !templates) {
        navigate("/");
        return;
      }
      if (isProductRoute && !products) {
        navigate("/");
        return;
      }
      if (isScreenRoute && !screens) {
        navigate("/");
        return;
      }
    } else {
      let authUser;
      try {
        authUser = JSON.parse(authOptions.authUser);
      } catch (error) {
        authUser = authOptions.authUser;
      }
      const dsokOperator = authUser?.type === 4;
      const pPath = typeof location.pathname === "string" ? location.pathname : "";
      const onPublicProcessedOrder =
        pPath === "/dsok/processed-order" ||
        pPath.startsWith("/dsok/processed-order/");
      if (
        dsokOperator &&
        location.pathname !== "/customer/dsok/orders" &&
        !onPublicProcessedOrder
      ) {
        navigate("/");
        return;
      }
    }
  }, [
    authOptions.authUser,
    isSuperAdminOnly,
    location?.pathname,
    isPresentationRoute,
    isKioskRoute,
    isTemplateRoute,
    isScreenRoute,
    isDsokRoute,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default JumboAuthProvider;
